import React, { useState, useRef, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { ChevronDown, Network, CheckCircle2 } from 'lucide-react';
import MainentIcon from '../../assets/home-assets/mainent.jsx';
import OptimismIcon from '../../assets/home-assets/optimisim.jsx';
import SepoliaIcon from '../../assets/home-assets/sepholia.jsx';

/**
 * Network switcher component that displays a dropdown for selecting networks
 */
const NetworkSwitcher = () => {
  const {
    isConnected,
    networkName,
    isCorrectNetwork,
    SUPPORTED_NETWORKS,
    switchNetwork,
    loading
  } = useWallet();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get network display info
  const getNetworkInfo = (networkKey) => {
    const network = SUPPORTED_NETWORKS[networkKey];
    return {
      name: network?.name || networkKey,
      currency: network?.currency?.symbol || 'ETH'
    };
  };

  // Get network color based on name
  const getNetworkColor = (networkKey) => {
    const colorMap = {
      'optimism': '#FF0420',
      'optimism-sepolia': '#FF0420',
      'sepolia': '#8A92B2',
      'mainnet': '#29B6AF',
      'hardhat': '#FFF100'
    };
    
    return colorMap[networkKey] || '#6366F1'; // Default to indigo color
  };

  // Get network image based on name
  const getNetworkImage = (networkKey) => {
    switch (networkKey) {
      case 'OPTIMISM':
        return <OptimismIcon/>;
      case 'OPTIMISM_SEPOLIA':
        return <SepoliaIcon/>;
      case 'SEPOLIA':
        return <OptimismIcon/>;
      default:
        return <MainentIcon/>;
    }
  };

  // Handle network switch
  const handleNetworkSwitch = async (networkKey) => {
    setIsOpen(false);
    await switchNetwork(networkKey);
  };

  // If not connected, don't show anything
  if (!isConnected) {
    return null;
  }

  const currentNetworkInfo = getNetworkInfo(networkName);
  const networkColor = getNetworkColor(networkName);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)} 
        className={`flex items-center rounded-full border-2 border-gray-200 pr-1 shadow cursor-pointer ${
          loading ? 'opacity-70 cursor-not-allowed' : ''
        } ${
          networkName === 'OPTIMISM' ? 'bg-red-500' : 'bg-black'
        }`}
      >
        <div className="w-9 h-9 rounded-full inline-flex items-center justify-center">
          {getNetworkImage(networkName)}
        </div>
        <ChevronDown 
          size={20} 
          className={`text-white font-bold transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100 transform transition-all duration-150 opacity-100 scale-100">
          <div className="py-1.5" role="menu" aria-orientation="vertical">
            {Object.keys(SUPPORTED_NETWORKS).map((key) => {
              const network = getNetworkInfo(key);
              const isActive = networkName === key;
              
              return (
                <button
                  key={key}
                  onClick={() => handleNetworkSwitch(key)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                  }`}
                  role="menuitem"
                >
                  <div className="flex items-center">
                    <div className="w-5 h-5 rounded-full bg-white border-2 border-gray-200 shadow mr-2 inline-flex items-center justify-center">
                      {getNetworkImage(key)}
                    </div>
                    <span>{network.name}</span>
                  </div>
                  
                  {isActive && (
                    <CheckCircle2 size={16} className="text-indigo-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkSwitcher; 